import { z } from 'zod';
import { DocumentType, type PeopleSheet } from './enums.js';

/**
 * People and approvals sheets (spec 9.1). Schemas normalise as they parse:
 * trim, uppercase IDs, lowercase UPNs, ISO dates, numeric amounts.
 */

const text = z.string({ required_error: 'Required' }).trim().min(1, 'Required');
const id = text.transform((s) => s.toUpperCase());
const upn = z.string({ required_error: 'Required' }).trim().toLowerCase().email('Not a valid UPN / email');
const isoDate = z
  .string({ required_error: 'Required' })
  .trim()
  .regex(/^\d{4}-\d{2}-\d{2}$/, 'Use yyyy-mm-dd')
  .refine((s) => !Number.isNaN(Date.parse(`${s}T00:00:00Z`)), 'Not a real date');
const bool = z.preprocess(
  (v) => {
    if (v === undefined || v === null || v === '') return true;
    if (typeof v === 'boolean') return v;
    const s = String(v).trim().toLowerCase();
    if (['true', 'yes', 'y', '1'].includes(s)) return true;
    if (['false', 'no', 'n', '0'].includes(s)) return false;
    return v;
  },
  z.boolean({ invalid_type_error: 'Use yes or no' }),
);
const amount = z.preprocess(
  (v) => (typeof v === 'string' ? Number(v.replace(/[$,\s]/g, '')) : v),
  z.number({ invalid_type_error: 'Must be a number' }).nonnegative('Cannot be negative'),
);
const blankToUndefined = (v: unknown): unknown => (v === '' || v === null ? undefined : v);
const optionalId = z.preprocess(blankToUndefined, id.optional());
const optionalText = z.preprocess(blankToUndefined, z.string().trim().min(1).optional());
const optionalEmail = z.preprocess(
  blankToUndefined,
  z.string().trim().toLowerCase().email('Not a valid email').optional(),
);
const optionalDate = z.preprocess(blankToUndefined, isoDate.optional());
const currency = z
  .string({ required_error: 'Required' })
  .trim()
  .toUpperCase()
  .regex(/^[A-Z]{3}$/, 'Use a 3-letter currency code (USD)');

/** Project types the projects sheet accepts; mapped to D365's ProjectType enum in entities.ts. */
export const ProjectType = z.enum(['Internal', 'Cost', 'TimeAndMaterial', 'FixedPrice'], {
  errorMap: () => ({ message: 'Use Internal, Cost, TimeAndMaterial or FixedPrice' }),
});
export type ProjectType = z.infer<typeof ProjectType>;
/** Project types billed to a customer, so they need a project contract. */
export const EXTERNAL_PROJECT_TYPES: readonly ProjectType[] = ['TimeAndMaterial', 'FixedPrice'];

const address = {
  street: optionalText,
  city: optionalText,
  state: optionalText,
  zip: optionalText,
};

export const PEOPLE_SCHEMAS = {
  users: z.object({ upn, displayName: text, email: upn }),
  workers: z.object({
    personnelNumber: id,
    firstName: text,
    lastName: text,
    upn,
    employmentStart: isoDate,
  }),
  positions: z.object({
    positionId: id,
    title: text,
    jobId: id,
    departmentId: optionalId,
    reportsToPositionId: id,
  }),
  assignments: z.object({
    positionId: id,
    personnelNumber: id,
    startDate: isoDate,
    primary: bool,
  }),
  approvalLimits: z.object({ jobId: id, documentType: DocumentType, amountUsd: amount }),
  roles: z.object({ upn, roleId: text }),
  customers: z.object({
    customerAccount: id,
    name: text,
    customerGroup: id,
    currency,
    phone: optionalText,
    email: optionalEmail,
    ...address,
    paymentTerms: optionalId,
  }),
  vendors: z.object({
    vendorAccount: id,
    name: text,
    vendorGroup: id,
    currency,
    phone: optionalText,
    email: optionalEmail,
    ...address,
    paymentTerms: optionalId,
    paymentMethod: optionalId,
  }),
  projectContracts: z.object({ contractId: id, name: text, customerAccount: id }),
  projects: z.object({
    projectId: id,
    name: text,
    projectType: ProjectType,
    projectGroup: id,
    contractId: optionalId,
    parentProjectId: optionalId,
    managerPersonnelNumber: optionalId,
    controllerPersonnelNumber: optionalId,
    startDate: isoDate,
    endDate: optionalDate,
  }),
} satisfies Record<PeopleSheet, z.ZodTypeAny>;

export type UserRow = z.infer<typeof PEOPLE_SCHEMAS.users>;
export type WorkerRow = z.infer<typeof PEOPLE_SCHEMAS.workers>;
export type PositionRow = z.infer<typeof PEOPLE_SCHEMAS.positions>;
export type AssignmentRow = z.infer<typeof PEOPLE_SCHEMAS.assignments>;
export type ApprovalLimitRow = z.infer<typeof PEOPLE_SCHEMAS.approvalLimits>;
export type RoleRow = z.infer<typeof PEOPLE_SCHEMAS.roles>;
export type CustomerRow = z.infer<typeof PEOPLE_SCHEMAS.customers>;
export type VendorRow = z.infer<typeof PEOPLE_SCHEMAS.vendors>;
export type ProjectContractRow = z.infer<typeof PEOPLE_SCHEMAS.projectContracts>;
export type ProjectRow = z.infer<typeof PEOPLE_SCHEMAS.projects>;

export interface SheetColumn {
  key: string;
  label: string;
  required: boolean;
  example: string;
}

export interface SheetDef {
  sheet: PeopleSheet;
  title: string;
  description: string;
  columns: SheetColumn[];
  /** Columns forming the duplicate key (rule P1). */
  key: string[];
}

const col = (key: string, label: string, example: string, required = true): SheetColumn => ({
  key,
  label,
  required,
  example,
});

export const SHEETS: SheetDef[] = [
  {
    sheet: 'users',
    title: 'Users',
    description: 'Who signs in. One row per Entra account (UPN); workers and roles point here by UPN.',
    columns: [
      col('upn', 'UPN', 'reese.dalton@rsmd365s.com'),
      col('displayName', 'Display name', 'Reese Dalton'),
      col('email', 'Email', 'reese.dalton@rsmd365s.com'),
    ],
    key: ['upn'],
  },
  {
    sheet: 'workers',
    title: 'Workers',
    description: 'The HR record for each person, keyed by personnel number and linked to their user by UPN.',
    columns: [
      col('personnelNumber', 'Personnel number', '046001'),
      col('firstName', 'First name', 'Reese'),
      col('lastName', 'Last name', 'Dalton'),
      col('upn', 'UPN', 'reese.dalton@rsmd365s.com'),
      col('employmentStart', 'Employment start', '2026-11-01'),
    ],
    key: ['personnelNumber'],
  },
  {
    sheet: 'positions',
    title: 'Positions',
    description:
      'The seats at the venue. Each has a job (which carries the approval limit) and reports to another position.',
    columns: [
      col('positionId', 'Position ID', 'V046-GM'),
      col('title', 'Title', 'General manager'),
      col('jobId', 'Job', 'VENUE-GM'),
      col('departmentId', 'Department', 'V046-OPS', false),
      col('reportsToPositionId', 'Reports to position', 'REG-FIN-01'),
    ],
    key: ['positionId'],
  },
  {
    sheet: 'assignments',
    title: 'Assignments',
    description:
      'Puts a person in a seat: personnel number (worker) + position ID. This is the link from person to position.',
    columns: [
      col('positionId', 'Position ID', 'V046-GM'),
      col('personnelNumber', 'Personnel number', '046001'),
      col('startDate', 'Start date', '2026-11-01'),
      col('primary', 'Primary', 'yes', false),
    ],
    key: ['positionId', 'personnelNumber'],
  },
  {
    sheet: 'approvalLimits',
    title: 'Approval limits',
    description:
      "How much each job can approve, per document type. A person's limit comes from their position's job. 0 means cannot approve.",
    columns: [
      col('jobId', 'Job', 'VENUE-GM'),
      col('documentType', 'Document type', 'PurchaseOrder'),
      col('amountUsd', 'Amount (USD)', '100000'),
    ],
    key: ['jobId', 'documentType'],
  },
  {
    sheet: 'roles',
    title: 'Roles',
    description: 'Security roles for a user (by UPN), scoped to the venue company.',
    columns: [
      col('upn', 'UPN', 'reese.dalton@rsmd365s.com'),
      col('roleId', 'Role', 'LEDGERACCOUNTINGMANAGER'),
    ],
    key: ['upn', 'roleId'],
  },
  {
    sheet: 'customers',
    title: 'Customers',
    description:
      'New customers created in the venue company over OData. Customers the template copy brings are not listed here.',
    columns: [
      col('customerAccount', 'Customer account', 'CUS-0001'),
      col('name', 'Name', 'Harborline Events Group'),
      col('customerGroup', 'Customer group', 'EVENTS'),
      col('currency', 'Currency', 'USD'),
      col('phone', 'Phone', '555-0142', false),
      col('email', 'Email', 'ap@harborline.example', false),
      col('street', 'Street', '200 Pier Avenue', false),
      col('city', 'City', 'Tulsa', false),
      col('state', 'State', 'OK', false),
      col('zip', 'ZIP', '74103', false),
      col('paymentTerms', 'Payment terms', 'NET30', false),
    ],
    key: ['customerAccount'],
  },
  {
    sheet: 'vendors',
    title: 'Vendors',
    description:
      'New vendors created in the venue company over OData, on top of the vendors the template copy brings.',
    columns: [
      col('vendorAccount', 'Vendor account', 'VEN-0001'),
      col('name', 'Name', 'Northgate Staffing Co'),
      col('vendorGroup', 'Vendor group', 'SERVICES'),
      col('currency', 'Currency', 'USD'),
      col('phone', 'Phone', '555-0187', false),
      col('email', 'Email', 'billing@northgate.example', false),
      col('street', 'Street', '18 Commerce Way', false),
      col('city', 'City', 'Tulsa', false),
      col('state', 'State', 'OK', false),
      col('zip', 'ZIP', '74107', false),
      col('paymentTerms', 'Payment terms', 'NET30', false),
      col('paymentMethod', 'Payment method', 'ACH', false),
    ],
    key: ['vendorAccount'],
  },
  {
    sheet: 'projectContracts',
    title: 'Project contracts',
    description:
      'Contracts that fund customer-billed projects. The customer (from the customers sheet or the template) is the funding source.',
    columns: [
      col('contractId', 'Contract ID', 'V046-PC-001'),
      col('name', 'Name', 'Naming rights 2027'),
      col('customerAccount', 'Customer account', 'CUS-0001'),
    ],
    key: ['contractId'],
  },
  {
    sheet: 'projects',
    title: 'Projects',
    description:
      'The opening set of projects. Time-and-material and fixed-price projects need a contract; manager and controller are personnel numbers from the workers sheet.',
    columns: [
      col('projectId', 'Project ID', 'V046-0001'),
      col('name', 'Name', 'Venue operations overhead'),
      col('projectType', 'Project type', 'Internal'),
      col('projectGroup', 'Project group', 'INTERNAL'),
      col('contractId', 'Contract ID', 'V046-PC-001', false),
      col('parentProjectId', 'Parent project', 'V046-0001', false),
      col('managerPersonnelNumber', 'Project manager', '046005', false),
      col('controllerPersonnelNumber', 'Project controller', '046002', false),
      col('startDate', 'Start date', '2026-11-01'),
      col('endDate', 'End date', '2027-10-31', false),
    ],
    key: ['projectId'],
  },
];

/** Tabs that hold master data (created over OData) rather than people and approvals. */
export const MASTER_DATA_SHEETS: readonly PeopleSheet[] = [
  'customers',
  'vendors',
  'projectContracts',
  'projects',
];

export const SHEET_BY_ID: Record<PeopleSheet, SheetDef> = Object.fromEntries(
  SHEETS.map((s) => [s.sheet, s]),
) as Record<PeopleSheet, SheetDef>;

/** Typed people data after parsing (only rows that parsed cleanly). */
export interface PeopleData {
  users: Array<UserRow & { rowNumber: number }>;
  workers: Array<WorkerRow & { rowNumber: number }>;
  positions: Array<PositionRow & { rowNumber: number }>;
  assignments: Array<AssignmentRow & { rowNumber: number }>;
  approvalLimits: Array<ApprovalLimitRow & { rowNumber: number }>;
  roles: Array<RoleRow & { rowNumber: number }>;
  customers: Array<CustomerRow & { rowNumber: number }>;
  vendors: Array<VendorRow & { rowNumber: number }>;
  projectContracts: Array<ProjectContractRow & { rowNumber: number }>;
  projects: Array<ProjectRow & { rowNumber: number }>;
}

export const PeopleRule = z.enum(['P1', 'P2', 'P3', 'P4', 'P5', 'P6', 'P7', 'P8', 'P9', 'P10', 'P11']);
export type PeopleRule = z.infer<typeof PeopleRule>;

export interface PeopleIssue {
  rule: PeopleRule;
  sheet: PeopleSheet | null;
  rowNumber: number | null;
  column: string | null;
  severity: 'error' | 'warning';
  message: string;
}

export const PEOPLE_RULE_TITLES: Record<PeopleRule, string> = {
  P1: 'Columns, formats and duplicates',
  P2: 'Accounts exist in Entra ID',
  P3: 'Reporting lines',
  P4: 'Position assignments',
  P5: 'Jobs and approval limits',
  P6: 'Security roles and segregation of duties',
  P7: 'IDs not used by another company',
  P8: 'Approval routing coverage',
  P9: 'Project managers and controllers',
  P10: 'Customers and vendors',
  P11: 'Projects and contracts',
};
